import React, { useCallback, useState, useRef, useEffect } from "react";
import Image from "next/image";
import { Close, Menu } from "@/assets/icons";
import { Fundview } from "@/assets/images";
import NavLink from "@/components/navlink/NavLink";

const navOptions = [
  { id: 1, href: "#whyFundview", label: "Why Fundview?" },
  { id: 2, href: "#useCases", label: "Uses Cases" },
  { id: 3, href: "#features", label: "Features" },
  { id: 4, href: "#pricing", label: "Pricing" },
  { id: 5, href: "#faq", label: "FAQs" },
];

const loginOptions = [
  {
    id: "broker",
    href: "/login?returnTo=/dashboard",
    title: "Broker Login",
    subtitle: "For mortgage brokers",
    icon: (
      <svg className="size-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
    gradient: "from-blue-500 to-indigo-600",
    hoverColor: "hover:bg-blue-50",
  },
  {
    id: "lender",
    href: "/capworks/login?returnTo=/capworks/dashboard",
    title: "Lender Login",
    subtitle: "For financial experts",
    icon: (
      <svg className="size-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    gradient: "from-emerald-500 to-teal-600",
    hoverColor: "hover:bg-emerald-50",
  },
];

function Navbar() {
  const [expanded, setExpanded] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleToggle = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const handleLoginDropdown = useCallback(() => {
    setShowLoginDropdown(!showLoginDropdown);
  }, [showLoginDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLoginDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 z-50 w-full border-b border-gray-100 bg-white/95 py-4 shadow-lg backdrop-blur-sm md:py-6">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="shrink-0">
            <NavLink
              href="/"
              className="flex rounded-lg outline-none ring-offset-2 transition-all duration-200 hover:scale-105 focus:ring-2 focus:ring-gray-900"
              label={<Image className="h-8 w-auto" src={Fundview} alt="Logo" />}
            />
          </div>

          <div className="flex lg:hidden">
            <button
              type="button"
              className="rounded-md p-2 text-gray-900 transition-colors duration-200 hover:bg-gray-100"
              onClick={handleToggle}
              aria-expanded={expanded}
            >
              {!expanded ? <Menu /> : <Close />}
            </button>
          </div>

          <div className="hidden lg:ml-16 lg:flex lg:items-center lg:justify-center lg:space-x-10 xl:space-x-16">
            {navOptions.map(({ href, label }) => (
              <NavLink
                key={label}
                href={href}
                label={label}
                className="relative rounded-lg px-3 py-2 text-base font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
              />
            ))}
          </div>

          <div className="hidden lg:ml-auto lg:flex lg:items-center lg:space-x-6">
            {/* Enhanced Login Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={handleLoginDropdown}
                className="group flex items-center space-x-2 rounded-lg bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-3 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-gray-700 hover:to-gray-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
              >
                <span>Login</span>
                <svg
                  className={`size-4 transition-transform duration-300 ${showLoginDropdown ? "rotate-180" : ""}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Enhanced Dropdown Menu */}
              {showLoginDropdown && (
                <div className="absolute right-0 z-50 mt-3 w-80 transition-all duration-300 ease-out">
                  <div className="overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 backdrop-blur-sm">
                    {/* Header */}
                    <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4">
                      <h3 className="text-lg font-semibold text-gray-900">Choose Your Portal</h3>
                      <p className="mt-1 text-sm text-gray-600">Select the login that matches your role</p>
                    </div>

                    {/* Login Options */}
                    <div className="p-2">
                      {loginOptions.map(({ id, href, title, subtitle, icon, hoverColor }) => (
                        <NavLink
                          key={id}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          label={
                            <div
                              className={`group/item flex items-center space-x-4 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-md ${hoverColor}`}
                            >
                              {/* Icon */}
                              <div className="shrink-0">
                                <div className="rounded-lg bg-white p-2 shadow-sm transition-shadow duration-300 group-hover/item:shadow-md">
                                  {icon}
                                </div>
                              </div>

                              {/* Content */}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between">
                                  <h4 className="group-hover:item:text-gray-800 font-semibold text-gray-900 transition-colors duration-200">
                                    {title}
                                  </h4>
                                  <svg
                                    className="group-hover:item:translate-x-1 group-hover:item:text-gray-600 size-4 text-gray-400 transition-all duration-200"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                    />
                                  </svg>
                                </div>
                                <p className="group-hover:item:text-gray-600 mt-1 text-sm text-gray-500 transition-colors duration-200">
                                  {subtitle}
                                </p>
                              </div>
                            </div>
                          }
                          className="block"
                        />
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-3">
                      <p className="text-center text-xs text-gray-500">
                        Need help?{" "}
                        <a
                          href="mailto:support@fundview.com?subject=Login Support Request&body=Hello, I need assistance with logging into my account."
                          className="cursor-pointer font-medium text-gray-700 hover:text-gray-900"
                        >
                          Contact Support
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Sign Up Button */}
            <NavLink
              key="sign-up"
              href="#signup"
              label="Sign Up"
              className="group relative inline-flex items-center justify-center overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-3 text-base font-bold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            />
          </div>
        </div>

        {/* Enhanced Mobile Menu */}
        {expanded && (
          <div className="lg:hidden">
            <div className="mt-6 rounded-xl bg-gray-50 p-4 shadow-inner">
              <nav className="space-y-2">
                {navOptions.map(({ href, label }) => (
                  <NavLink
                    key={href}
                    href={href}
                    label={label}
                    className="block rounded-lg px-4 py-3 text-base font-medium text-gray-700 transition-all duration-200 hover:bg-white hover:text-gray-900 hover:shadow-sm"
                  />
                ))}

                {/* Mobile Login Options */}
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <h4 className="px-4 py-2 text-sm font-semibold uppercase tracking-wider text-gray-500">
                    Login Options
                  </h4>
                  {loginOptions.map(({ id, href, title, subtitle, icon, hoverColor }) => (
                    <NavLink
                      key={id}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      label={
                        <div
                          className={`flex items-center space-x-3 rounded-lg p-4 transition-all duration-200 hover:shadow-sm ${hoverColor}`}
                        >
                          <div className="rounded-lg bg-white p-2 shadow-sm">{icon}</div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="font-semibold text-gray-900">{title}</div>
                              <svg
                                className="size-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </div>
                            <div className="text-sm text-gray-500">{subtitle}</div>
                          </div>
                        </div>
                      }
                      className="block"
                    />
                  ))}
                </div>

                {/* Mobile Sign Up */}
                <div className="pt-4">
                  <NavLink
                    key="sign-up"
                    href="#signup"
                    label="Sign Up"
                    className="block w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-center text-base font-bold text-white shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl"
                  />
                </div>
              </nav>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Navbar;
